import type { InsuranceEstimate } from '../../models/insuranceEstimate'
import type { InsuranceIndication } from '../../models/insuranceIndication'
import { InsuranceIndicationStatus, Vendor } from '../../models/insuranceTypes'

/**
 * A normalized carrier result card. Live indications and web-funnel estimates
 * are two different backend models; the funnel merges them into this one shape
 * so dedupe, sorting, the range bar, and the "Most competitive" tagline can
 * reason across both together.
 */
export interface CarrierCard {
  id: string
  vendor: Vendor
  vendorName: string
  status: InsuranceIndicationStatus
  iconUrl: string
  iconAlt: string
  /**
   * The sub-carrier behind a broker (e.g. Progressive behind Rocklake), i.e. the
   * assigned carrier record's name — null when the row has no carrier record and
   * so displays the vendor itself.
   */
  carrierName: string | null
  truckCount: number | null
  perTruckLow: number | null
  perTruckHigh: number | null
  fleetLow: number | null
  fleetHigh: number | null
  carrierNote: string | null
  /** The carrier's own curated note (e.g. a pre-bind requirement) — independent of carrierNote. */
  carrierPublicNote: string | null
  declineReason: string | null
  isCurrentInsurer: boolean
  tagline: string | null
}

export function indicationToCard(ind: InsuranceIndication): CarrierCard {
  return {
    id: ind.id,
    vendor: ind.vendor,
    vendorName: ind.vendorName,
    status: ind.status,
    iconUrl: ind.iconUrl,
    iconAlt: ind.iconAlt,
    carrierName: ind.carrierId ? ind.vendorName : null,
    truckCount: (ind.metadata.truckCount as number | undefined) ?? null,
    perTruckLow: ind.perTruckLow,
    perTruckHigh: ind.perTruckHigh,
    fleetLow: ind.fleetLow,
    fleetHigh: ind.fleetHigh,
    carrierNote: ind.carrierNote,
    carrierPublicNote: ind.carrierPublicNote,
    declineReason: ind.declineReason,
    isCurrentInsurer: ind.isCurrentInsurer,
    tagline: null,
  }
}

export function estimateToCard(est: InsuranceEstimate): CarrierCard {
  return {
    id: est.id,
    vendor: est.vendor,
    vendorName: est.vendorName,
    status: est.status,
    iconUrl: est.iconUrl,
    iconAlt: est.iconAlt,
    carrierName: est.carrierId ? est.vendorName : null,
    truckCount: est.truckCount,
    perTruckLow: est.perTruckLow,
    perTruckHigh: est.perTruckHigh,
    fleetLow: est.fleetLow,
    fleetHigh: est.fleetHigh,
    carrierNote: est.carrierNote,
    // InsuranceEstimate never had this field — its carrierNote never fell back to a
    // carrier's public_note in the first place, so there's nothing to surface here.
    carrierPublicNote: null,
    declineReason: est.declineReason || null,
    isCurrentInsurer: est.isCurrentInsurer,
    tagline: null,
  }
}

/**
 * The funnel can briefly surface a duplicate carrier (a PENDING shell plus the
 * resolved card) when a vendor's async task races confirm's commit. Hide Corgi
 * (not part of the funnel carrier set) and collapse each carrier to its single
 * best-status card so we never show a stuck PENDING next to its resolved twin.
 */
const STATUS_RANK: Record<string, number> = {
  [InsuranceIndicationStatus.GENERATED]: 0,
  [InsuranceIndicationStatus.PENDING]: 1,
  [InsuranceIndicationStatus.UNAVAILABLE]: 2,
  [InsuranceIndicationStatus.DECLINED]: 2,
  [InsuranceIndicationStatus.ERROR]: 2,
}

export function dedupeCarriers(cards: CarrierCard[]): CarrierCard[] {
  const byCarrier = new Map<string, CarrierCard>()
  for (const card of cards) {
    if (card.vendor === Vendor.CORGI) continue
    const key = `${card.vendor}:${card.carrierName ?? ''}`
    const existing = byCarrier.get(key)
    const rank = STATUS_RANK[card.status] ?? 9
    if (!existing || rank < (STATUS_RANK[existing.status] ?? 9)) {
      byCarrier.set(key, card)
    }
  }
  return [...byCarrier.values()]
}

/** Adaptive card tagline (spec §3.3), computed across the merged card set. */
export function taglineFor(card: CarrierCard, bestLow: number | null): string | null {
  if (card.status !== InsuranceIndicationStatus.GENERATED) return null
  if (card.isCurrentInsurer) return 'Current insurance provider'
  if (bestLow != null && card.perTruckLow != null && card.perTruckLow === bestLow) {
    return 'Most competitive'
  }
  return null
}
